import { useEffect, useState } from 'react';
import { Page, PageHero } from './PublicLayout';
import api from '../../services/api';
import API from '../../config/apiEndpoints';

const cache = {};

// Plain-text CMS content: blank lines separate paragraphs, "## " starts a heading. Never rendered as HTML.
function CmsContent({ content }) {
  const blocks = String(content || '').replace(/\r\n/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return (
    <article className="prose">
      {blocks.map((b, i) => (b.startsWith('## ')
        ? <h2 key={i}>{b.slice(3)}</h2>
        : <p key={i} style={{ whiteSpace: 'pre-line' }}>{b}</p>))}
    </article>
  );
}

/**
 * Shows the published CMS page for `slug` when an admin has created one; otherwise the built-in page (children).
 * The last result for the slug is shown straight away, but it is always re-fetched so admin edits appear without a reload.
 */
export default function CmsOverride({ slug, kicker, children }) {
  const [page, setPage] = useState(cache[slug] ?? null);

  useEffect(() => {
    let active = true;
    setPage(cache[slug] ?? null);
    api.get(API.cms.page(slug), { skipAuth: true, skipAuthRefresh: true, params: { _: Date.now() } })
      .then((p) => {
        cache[slug] = p && p.isPublished ? p : false;
        if (active) setPage(cache[slug]);
      })
      .catch((err) => {
        // 404 means no published page: use the built-in one. Keep the last good copy on network errors.
        if (err?.status === 404) cache[slug] = false;
        if (active) setPage(cache[slug] ?? false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (!page) return children;

  return (
    <Page title={page.title}>
      <PageHero kicker={kicker || page.title} title={page.title} text={page.metaDescription || undefined}>
        <p className="page-hero__meta">Last updated {new Date(page.updatedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </PageHero>
      <section className="section" style={{ paddingTop: 56 }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <CmsContent content={page.content} />
        </div>
      </section>
    </Page>
  );
}
